// ===============================================
// 📊 MONITORING API SERVICE
// ===============================================
// File: src/services/monitoringApiService.ts

const API_BASE = import.meta.env.VITE_API_URL || 'https://app.ingeai7.com/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('kikai_token') || 
                localStorage.getItem('token') ||
                sessionStorage.getItem('token');
  
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

// ===============================================
// 🔄 REFRESH TOKEN AUTOMÁTICO
// ===============================================
const refreshTokenIfNeeded = async () => {
  const refreshToken = localStorage.getItem('kikai_refresh_token');
  if (!refreshToken) {
    console.warn('⚠️ No refresh token available');
    return false;
  }

  try {
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken })
    });

    if (!response.ok) {
      throw new Error('Refresh token failed');
    }

    const data = await response.json();
    if (data.accessToken) {
      localStorage.setItem('kikai_token', data.accessToken);
      console.log('✅ Token refreshed successfully');
      return true;
    }

    return false;
  } catch (error) {
    console.error('❌ Error refreshing token:', error);
    // Redirigir a login si falla
    localStorage.clear();
    window.location.href = '/login';
    return false;
  }
};

// ===============================================
// 🔧 FETCH CON RETRY Y REFRESH AUTOMÁTICO
// ===============================================
const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
  const headers = getAuthHeaders();
  
  let response = await fetch(url, {
    ...options,
    headers: { ...headers, ...options.headers }
  });

  // Si es 401/403, intentar refresh token
  if (response.status === 401 || response.status === 403) {
    console.log('🔄 Token expired, attempting refresh...');
    const refreshed = await refreshTokenIfNeeded();
    
    if (refreshed) {
      // Reintentar request con nuevo token
      const newHeaders = getAuthHeaders();
      response = await fetch(url, {
        ...options,
        headers: { ...newHeaders, ...options.headers }
      });
    }
  }

  return response;
};

export interface UserActivity {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  user_role: string;
  session_id: string;
  activity_type: 'login' | 'logout' | 'token_refresh' | 'password_change' | 'profile_update' | 'api_call' | 'create_ticket' | 'chat_search' | 'download_document' | 'search_parts';
  ip_address: string;
  user_agent: string;
  country_code: string;
  city: string;
  metadata: string;
  created_at: string;
}

export interface RegisteredUser {
  id: number;
  name: string;
  username: string;
  email: string;
  role: string;
  status: string;
  subscription_status: string;
  subscription_type: string;
  subscription_plan: string;
  is_premium: boolean;
  created_at: string;
  updated_at: string;
  last_login: string;
  login_count: number;
  daily_queries_used: number;
  daily_queries_limit: number;
  country: string;
  company: string;
  phone: string;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  stats?: any;
}

export const monitoringService = {
  // 📊 Obtener actividades de usuarios
  async getUserActivities(params: {
    page?: number;
    limit?: number;
    user_id?: number;
    activity_type?: string;
    date_from?: string;
    date_to?: string;
  } = {}): Promise<PaginatedResponse<UserActivity>> {
    const queryParams = new URLSearchParams(
      Object.entries(params).reduce((acc, [key, value]) => {
        if (value !== undefined) acc[key] = String(value);
        return acc;
      }, {} as Record<string, string>)
    );

    const response = await fetchWithAuth(
      `${API_BASE}/monitoring/user-activities?${queryParams}`
    );

    if (!response.ok) {
      throw new Error(`Error loading activities: ${response.status}`);
    }

    return response.json();
  },

  // 👥 Obtener usuarios registrados
  async getRegisteredUsers(params: {
    page?: number;
    limit?: number;
    status?: string;
    role?: string;
    subscription_status?: string;
    search?: string;
  } = {}): Promise<PaginatedResponse<RegisteredUser>> {
    const queryParams = new URLSearchParams(
      Object.entries(params).reduce((acc, [key, value]) => {
        if (value !== undefined) acc[key] = String(value);
        return acc;
      }, {} as Record<string, string>)
    );

    const response = await fetchWithAuth(
      `${API_BASE}/monitoring/registered-users?${queryParams}`
    );

    if (!response.ok) {
      throw new Error(`Error loading users: ${response.status}`);
    }

    return response.json();
  },
  
  async getUserLimits(): Promise<any> {
    const response = await fetchWithAuth(
      `${API_BASE}/user/limits`
    );

    if (!response.ok) {
      throw new Error(`Error loading user limits: ${response.status}`);
    }

    return response.json();
  },

  // 📈 Obtener resumen de actividades
  async getActivitySummary(): Promise<any> {
    const response = await fetchWithAuth(
      `${API_BASE}/monitoring/activity-summary`
    );

    if (!response.ok) {
      throw new Error(`Error loading summary: ${response.status}`);
    }

    return response.json();
  },

  // 📥 Exportar actividades
  async exportActivities(format: 'excel' | 'csv', filters: any = {}): Promise<void> {
    const response = await fetchWithAuth(
      `${API_BASE}/monitoring/export/activities`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ format, filters })
      }
    );

    if (!response.ok) {
      throw new Error(`Error exporting activities: ${response.status}`);
    }

    // Descargar archivo
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `actividades_${Date.now()}.${format === 'excel' ? 'xlsx' : 'csv'}`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },

  // 📥 Exportar usuarios
  async exportUsers(format: 'excel' | 'csv', filters: any = {}): Promise<void> {
    const response = await fetchWithAuth(
      `${API_BASE}/monitoring/export/users`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ format, filters })
      }
    );

    if (!response.ok) {
      throw new Error(`Error exporting users: ${response.status}`);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `usuarios_${Date.now()}.${format === 'excel' ? 'xlsx' : 'csv'}`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
};